import { toast } from "react-toastify";
import { call, put, takeLatest } from "redux-saga/effects";
import { getStudentExamApiError } from "../../../utils/studentExamPaths";
import { saveTagExamSession, unwrapTagExamCreate } from "../../../utils/studentTagExam";
import * as actions from "./Actions";
import * as api from "./Api";
import Types from "./Types";

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

function* createStudentExam({ payload, action }) {
  try {
    yield put(actions.addStudentExamsLoading(true));
    const result = yield call(api.createStudentExams, payload);

    yield put(actions.createStudentExamsSuccess(result.data));
    if (action) {
      action();
    } else {
      window.location.reload();
    }
  } catch (error) {
    toast.error(errorMessage(error, "Could not create exam"));
    yield put(actions.createStudentExamsFail());
  } finally {
    yield put(actions.addStudentExamsLoading(false));
  }
}

function* createTagExam({ payload, action }) {
  try {
    yield put(actions.addStudentExamsCreating(true));
    const result = yield call(api.createTagExam, payload);
    const created = unwrapTagExamCreate(result.data);
    if (!created.exam?.id) {
      throw new Error("Exam was created but no exam id was returned");
    }
    if (!created.questions.length) {
      throw new Error("Exam was created but no questions were returned");
    }
    yield put(actions.createTagExamSuccess(created));
    saveTagExamSession(created);
    if (action) action(created);
  } catch (error) {
    toast.error(errorMessage(error, "Could not create practice exam"));
    yield put(actions.createTagExamFail());
  } finally {
    yield put(actions.addStudentExamsCreating(false));
  }
}

function* getStudentExams() {
  try {
    yield put(actions.addStudentExamsLoading(true));
    const result = yield call(api.getStudentExams);
    yield put(actions.getStudentExamsSuccess(result.data));
  } catch (error) {
    toast.error(errorMessage(error, "Could not load practice exams"));
    yield put(actions.getStudentExamsFail());
  } finally {
    yield put(actions.addStudentExamsLoading(false));
  }
}

function* getStudentTags() {
  try {
    yield put(actions.addStudentTagsLoading(true));
    const result = yield call(api.getStudentTags);
    yield put(actions.getStudentTagsSuccess(result.data));
  } catch (error) {
    toast.error(errorMessage(error, "Could not load tags"));
    yield put(actions.getStudentTagsFail());
  } finally {
    yield put(actions.addStudentTagsLoading(false));
  }
}

function* getStudentExamQuestions({ payload }) {
  try {
    yield put(actions.addStudentExamsLoading(true));
    const result = yield call(api.getStudentExamQuestions, payload);
    yield put(actions.getStudentExamQuestionsSuccess(result.data));
  } catch (error) {
    const { message, alreadyAnswered } = getStudentExamApiError(error);
    toast.error(message);
    yield put(
      actions.getStudentExamQuestionsFail({
        alreadyAnswered,
        message,
      })
    );
  } finally {
    yield put(actions.addStudentExamsLoading(false));
  }
}

export default function* studentExamSaga() {
  yield takeLatest(Types.CREATE_STUDENT_EXAMS_REQUEST, createStudentExam);
  yield takeLatest(Types.CREATE_TAG_EXAM_REQUEST, createTagExam);
  yield takeLatest(Types.GET_STUDENT_EXAMS_REQUEST, getStudentExams);
  yield takeLatest(Types.GET_STUDENT_TAGS_REQUEST, getStudentTags);
  yield takeLatest(
    Types.GET_STUDENT_EXAM_QUESTIONS_REQUEST,
    getStudentExamQuestions
  );
}
